import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import CTASection from "@/components/CTASection";
import { CASE_STUDIES } from "@/data/content";

export default function CaseStudies() {
  const industries = ["All", ...Array.from(new Set(CASE_STUDIES.map((c) => c.industry)))];
  const [f, setF] = useState("All");
  const list = f === "All" ? CASE_STUDIES : CASE_STUDIES.filter((c) => c.industry === f);

  return (
    <>
      <SEO title="Engagement Examples" description="Anonymised, industry-based examples of enterprise database engineering, cloud migration and performance engineering engagements." />

      <section className="bg-[var(--dm-navy)] text-white">
        <div className="dm-container py-24 lg:py-32">
          <div className="dm-overline text-[var(--dm-emerald)]">Engagement examples</div>
          <h1 className="font-serif text-5xl sm:text-6xl mt-6 max-w-4xl leading-[1.05]">Anonymised examples of enterprise database engagements.</h1>
          <p className="text-white/70 text-lg mt-8 max-w-2xl">To respect client confidentiality and non-disclosure obligations, engagement examples on this page are anonymised and described at industry level. Detailed references are shared with client approval during the proposal process.</p>
        </div>
      </section>

      <section className="bg-white sticky top-20 z-30 border-b border-[var(--dm-border)]">
        <div className="dm-container py-4 flex gap-2 overflow-x-auto">
          {industries.map((i) => (
            <button key={i} data-testid={`case-filter-${i}`} onClick={() => setF(i)}
              className={`px-4 py-2 text-xs uppercase tracking-[0.18em] font-semibold whitespace-nowrap transition-colors ${f === i ? 'bg-[var(--dm-navy)] text-white' : 'text-[var(--dm-muted)] hover:text-[var(--dm-navy)]'}`}
            >{i}</button>
          ))}
        </div>
      </section>

      <section className="bg-white">
        <div className="dm-container dm-section grid md:grid-cols-2 gap-8">
          {list.map((c) => (
            <Link key={c.slug} to={`/case-studies/${c.slug}`} data-testid={`case-card-${c.slug}`} className="group border border-[var(--dm-border)] hover:border-[var(--dm-navy)] bg-white">
              <div className="aspect-[16/9] overflow-hidden">
                <img src={c.image} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-8">
                <div className="flex items-center justify-between">
                  <div className="dm-overline">{c.industry}</div>
                  <div className="font-mono text-sm text-[var(--dm-emerald)]">{c.metric}</div>
                </div>
                <div className="font-serif text-2xl text-[var(--dm-navy)] mt-4 leading-snug">{c.title}</div>
                <div className="text-sm text-[var(--dm-muted)] mt-3 leading-relaxed">{c.summary}</div>
                <div className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-[var(--dm-navy)]">
                  Read the case study <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <CTASection />
    </>
  );
}
